import { TICK_RATE, type GameState } from '@cuboom/shared';
import { fullscreenSupported, isFullscreen, onFullscreenChange, toggleFullscreen } from './fullscreen.js';

/**
 * Top status strip: active character, phase, turn countdown.
 * Pure DOM, updated once per frame. pointer-events:none so it never eats
 * canvas gestures.
 */
export class Hud {
  readonly root: HTMLDivElement;
  private readonly dot: HTMLSpanElement;
  private readonly nameEl: HTMLSpanElement;
  private readonly phaseEl: HTMLSpanElement;
  private readonly timerFill: HTMLSpanElement;
  private readonly timerText: HTMLSpanElement;
  private readonly turnEl: HTMLSpanElement;
  /** Background-music power (optional — the button only shows when wired). */
  private readonly music: { isOn(): boolean; toggle(): boolean } | null;
  private musicBtn: HTMLButtonElement | null = null;
  private musicShown = false;
  /** Fullscreen toggle — only when the platform allows pages fullscreen. */
  private fsBtn: HTMLButtonElement | null = null;

  constructor(
    parent: HTMLElement,
    totalTurnS: number,
    onMenu?: () => void,
    music?: { isOn(): boolean; toggle(): boolean },
  ) {
    this.music = music ?? null;
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:absolute;top:8px;left:50%;transform:translateX(-50%);' +
      'display:flex;align-items:center;gap:12px;padding:7px 14px;' +
      'background:rgba(8,12,20,.55);border-radius:10px;color:#fff;' +
      'font:12px/1.4 system-ui;pointer-events:none;z-index:5;white-space:nowrap;' +
      'max-width:96vw;overflow:hidden';

    this.dot = document.createElement('span');
    this.dot.style.cssText = 'width:11px;height:11px;border-radius:50%;background:#888;flex:none';

    this.nameEl = document.createElement('span');
    this.nameEl.style.cssText = 'font-weight:700';

    this.phaseEl = document.createElement('span');
    this.phaseEl.style.cssText = 'opacity:.75;text-transform:uppercase;font-size:10px;letter-spacing:.08em';

    this.turnEl = document.createElement('span');
    this.turnEl.style.cssText = 'opacity:.6;font-size:10px';

    // Turn timer: track + fill + seconds readout.
    const timer = document.createElement('span');
    timer.style.cssText = 'position:relative;width:110px;height:10px;background:rgba(255,255,255,.14);border-radius:5px;overflow:hidden;flex:none';
    this.timerFill = document.createElement('span');
    this.timerFill.style.cssText = 'position:absolute;inset:0;transform-origin:left;background:#5ad85a;border-radius:5px';
    timer.appendChild(this.timerFill);

    this.timerText = document.createElement('span');
    this.timerText.style.cssText = 'font-variant-numeric:tabular-nums;min-width:30px;text-align:right';

    this.root.append(
      this.dot,
      this.nameEl,
      this.phaseEl,
      this.turnEl,
      timer,
      this.timerText,
    );

    // The only chrome buttons in the play view: music power + the☰ that
    // opens the text menus. Everything that plays the game stays on the
    // icon toolbar.
    const m = music;
    if (m) {
      const musicBtn = document.createElement('button');
      musicBtn.type = 'button';
      musicBtn.style.cssText =
        'pointer-events:auto;padding:2px 8px;border-radius:7px;cursor:pointer;' +
        'border:1px solid rgba(255,255,255,.28);background:rgba(255,255,255,.08);color:#fff;font:13px/1.3 system-ui';
      musicBtn.addEventListener('click', () => {
        m.toggle();
        this.paintMusic();
        musicBtn.blur();
      });
      this.musicBtn = musicBtn;
      this.root.appendChild(musicBtn);
      this.paintMusic();
      this.root.style.pointerEvents = 'auto';
    }
    // Full screen, right beside the sound power: one tap enters, one tap
    // leaves, mid-fight. Hidden where the platform refuses page fullscreen
    // (iPhone Safari) so the strip never shows a dead button.
    if (fullscreenSupported()) {
      const fsBtn = document.createElement('button');
      fsBtn.type = 'button';
      fsBtn.style.cssText =
        'pointer-events:auto;padding:2px 8px;border-radius:7px;cursor:pointer;' +
        'border:1px solid rgba(255,255,255,.28);background:rgba(255,255,255,.08);color:#fff;font:13px/1.3 system-ui';
      fsBtn.addEventListener('click', () => {
        toggleFullscreen();
        fsBtn.blur();
      });
      this.fsBtn = fsBtn;
      this.root.appendChild(fsBtn);
      // The menu row, Esc, or this button can flip the state — repaint from
      // the event, never from the click itself (the promise settles later).
      onFullscreenChange(() => this.paintFs());
      this.paintFs();
      this.root.style.pointerEvents = 'auto';
    }
    if (onMenu) {
      const menuBtn = document.createElement('button');
      menuBtn.type = 'button';
      menuBtn.textContent = '☰';
      menuBtn.title = 'Menu';
      menuBtn.style.cssText =
        'pointer-events:auto;margin-left:6px;padding:2px 8px;border-radius:7px;cursor:pointer;' +
        'border:1px solid rgba(255,255,255,.28);background:rgba(255,255,255,.08);color:#fff;font:13px/1.3 system-ui';
      menuBtn.addEventListener('click', () => {
        onMenu();
        menuBtn.blur();
      });
      this.root.appendChild(menuBtn);
      this.root.style.pointerEvents = 'auto';
    }
    parent.appendChild(this.root);

    this.totalTurnS = totalTurnS;
  }

  private readonly totalTurnS: number;

  /** Reflect the engine's music flag on the button (🎵 on / 🔇 off). */
  private paintMusic(): void {
    if (!this.musicBtn || !this.music) return;
    const on = this.music.isOn();
    this.musicShown = on;
    this.musicBtn.textContent = on ? '🎵' : '🔇';
    this.musicBtn.title = on ? 'Music: on' : 'Music: off';
    this.musicBtn.setAttribute('aria-pressed', String(on));
  }

  /**
   * Paint the fullscreen toggle (⛶). The glyph stays put — it means "screen
   * corners" either way — and the pressed tint carries the state, so there is
   * no glyph lottery across phone fonts.
   */
  private paintFs(): void {
    if (!this.fsBtn) return;
    const on = isFullscreen();
    this.fsBtn.textContent = '⛶';
    this.fsBtn.title = on ? 'Exit full screen' : 'Full screen';
    this.fsBtn.setAttribute('aria-pressed', String(on));
    this.fsBtn.style.background = on ? 'rgba(126,200,227,.32)' : 'rgba(255,255,255,.08)';
    this.fsBtn.style.borderColor = on ? 'rgba(150,215,255,.75)' : 'rgba(255,255,255,.28)';
  }

  update(state: GameState): void {
    // The menu row can flip music too — catch up if the flag changed.
    if (this.musicBtn && this.music && this.music.isOn() !== this.musicShown) this.paintMusic();

    const active = state.entities.find((e) => e.id === state.activeEntityId);
    const team = active ? state.teams.find((t) => t.id === active.teamId) : null;

    this.dot.style.background = active ? team?.color ?? '#888' : '#888';
    this.nameEl.textContent = active ? active.name : '—';
    this.phaseEl.textContent = state.phase;
    this.turnEl.textContent = `t${state.turnIndex}`;

    // Countdown against the absolute phase deadline; hide it outside play.
    const remain = Math.max(0, (state.phaseEndsAt - state.tick) / TICK_RATE);
    const frac = Math.min(1, remain / this.totalTurnS);
    this.timerFill.style.transform = `scaleX(${frac.toFixed(3)})`;
    this.timerFill.style.background = frac > 0.5 ? '#5ad85a' : frac > 0.2 ? '#e8c24a' : '#e04f3f';
    this.timerText.textContent = state.phase === 'aiming' || state.phase === 'resolving' ? `${remain.toFixed(0)}s` : '';
  }
}
